import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Grid,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from "@mui/material";
import {
  ListingHeader,
  ContainerCard,
  TableMenuButton,
} from "../../components";
import { AddQuote } from "../popups/AddQuote";
import { makeStyles } from "@mui/styles";
import { api, urls } from "../../services";
import { useAuth, useToast } from "../../contexts";
import moment from "moment";
import { debounce } from "lodash";
import NoDataCard from "../../components/cards/NoDataCard";
import LoaderCard from "../../components/cards/LoaderCard";
import { ConfirmationDialog } from "../../components/dialogs/ConfirmationDialog";

const useStyles = makeStyles((theme) => ({
  statusItem: {
    "&.MuiTableCell-root": {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      minHeight: 60,
    },
  },

  header: {
    fontSize: "16px !important",
    fontWeight: "600 !important",
  },
  // headItem: {
  //     fontWeight : 700
  //  }
}));

const menuOptions1 = [
  { id: 1, label: "Edit" },
  { id: 2, label: "Delete" },
];

export function Quotes() {
  const classes = useStyles();
  const [openAddQuote, setOpenAddQuote] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPage, setTotalPages] = useState(0);

  const [searchInput, setSearchInput] = useState("");

  const { showToast } = useToast();
  const { logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [selQuote, setSelQuote] = useState(null);
  const [confirmDelQuote, setConfirmDelQuote] = useState(false);

  const getQuotes = () => {
    setIsLoading(true);
    api
      .get(urls.quotes, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search_key: searchInput,
        },
      })
      .then((res) => {
        setQuotes(res.data.data.quotes);
        setTotalPages(res.data.data.pagination.total);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  };

  const getLanguages = () => {
    api
      .get(urls.languages)
      .then((res) => setLanguages(res.data.data))
      .catch((err) => {
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  };

  useEffect(() => {
    getLanguages();
  }, []);

  useEffect(() => {
    getQuotes();
  }, [page, rowsPerPage, searchInput]);

  const delayedSearch = useCallback(
    debounce((text) => {
      setSearchInput(text);
    }, 500),
    []
  );

  const handleOnSearch = (event) => {
    delayedSearch(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    getQuotes();
  };
  const handleCloseAddQuote = () => {
    setOpenAddQuote(false);
    setSelQuote(null);
  };
  const handleOnClickAddQuote = () => {
    setOpenAddQuote(true);
  };

  const onCloseDeletion = () => {
    setSelQuote(null);
    setConfirmDelQuote(false);
  };

  const onConfirmDeletion = () => {
    setConfirmDelQuote(false);
    api
      .delete(urls.deleteQuote + selQuote.id)
      .then((res) => {
        showToast("Quote deleted successfully", "success");
        getQuotes();
        setSelQuote(null);
      })
      .catch((err) => {
        setSelQuote(null);
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  };

  return (
    <Fragment>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <ListingHeader
            title={totalPage > 0 ? `Quotes (${totalPage})` : "Quotes"}
            btnTitle="Add New Quote"
            placeholder="Search Quotes"
            onClickPrimary={handleOnClickAddQuote}
            onSearchChange={handleOnSearch}
          />
        </Grid>
        {isLoading && (
          <Grid item>
            <LoaderCard />
          </Grid>
        )}
        {!isLoading && (
          <Grid item>
            <ContainerCard>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.header}>
                        Quote Title
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quotes.map((post, i) => {
                      return (
                        <TableRow key={i}>
                          <TableCell>{post.title_name}</TableCell>
                          <TableCell>
                            <TableMenuButton
                              options={menuOptions1}
                              onOptionClick={(menu) => {
                                setSelQuote(post);
                                if (menu.id === 1) {
                                  setOpenAddQuote(true);
                                } else {
                                  setConfirmDelQuote(true);
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPage === 0 && <NoDataCard title="No Data Found" />}
              <TablePagination
                component="div"
                count={totalPage}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 20, 30]}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </ContainerCard>
          </Grid>
        )}
      </Grid>
      {openAddQuote && (
        <AddQuote
          open={openAddQuote}
          onClose={handleCloseAddQuote}
          languages={languages}
          onSubmit={() => {
            handleCloseAddQuote();
            getQuotes();
          }}
          isEdit={Boolean(selQuote)}
          selQuote={selQuote}
        />
      )}
      {confirmDelQuote && (
        <ConfirmationDialog
          open={confirmDelQuote}
          onClose={onCloseDeletion}
          message="Are you sure you want to delete this Quote?"
          onSubmit={onConfirmDeletion}
        />
      )}
    </Fragment>
  );
}
